// This script is created and maintained by The Global Chat Team at The Estee Lauder Companies. Please raise any issues or feature requests with our team directly.

import {
  loadLivePersonAssets,
  loadCookieGatedCoreAssets,
  loadBrandAssets,
  loadChatUiAssets,
  performLocalSetup,
} from "../../utils/asset-loader-utils"

import {
  LiveChatError
} from "../../utils/common-utils";

function isExcludedRoute(currentPath: string, excludedRoutes: string[]): boolean {
  if (!excludedRoutes || !Array.isArray(excludedRoutes)) {
    return false;
  }

  return excludedRoutes.some(pattern => {
    const regexPattern = pattern
      // Escape special regex characters except *
      .replace(/[.+?^${}()|[\]\\]/g, '\\$&')
      // Replace * with regex pattern for any characters
      .replace(/\*/g, '.*')
      // Anchor to start and end
      .replace(/^/, '^')
      .replace(/$/, '$');

    const regex = new RegExp(regexPattern);
    return regex.test(currentPath);
  });
}

export async function loadUiResources(manifest: Manifest, resources: Resources, bucketPath: string) {
  if (resources.ui && resources.ui.enabled) {
    let excludedRoutes = resources.ui.excluded_routes || [];
    const globalExclusions = ["/checkout*", "/cart*", "/iframe*"];
    excludedRoutes.push(...globalExclusions);

    const currentPath = window.location.pathname;

    if (!isExcludedRoute(currentPath, excludedRoutes)) {
      const liveChatContainer = document.createElement('div');
      liveChatContainer.id = 'live-chat-ui-container';
      document.body.appendChild(liveChatContainer);

      const beautyDrawer = document.createElement("div");
      beautyDrawer.id = "livechat_lp_drawer_beauty";
      liveChatContainer.appendChild(beautyDrawer);

      const customerSupportDrawer = document.createElement("div");
      customerSupportDrawer.id = "livechat_lp_drawer_customer_support";
      liveChatContainer.appendChild(customerSupportDrawer);

      await loadChatUiAssets(manifest.ui, bucketPath);
    }
  }
}

export async function loadLivePersonResources(manifest: Manifest, resources: Resources, bucketPath: string) {
  if (resources.liveperson && resources.liveperson.enabled) {
    await loadLivePersonAssets(manifest.liveperson, bucketPath);
    loadCookieGatedCoreAssets(manifest.asset_loader, bucketPath);
  }
}

export async function fetchAndLoadBrandAssets(
  resources: Resources,
  api: LiveChatApi,
  bucketPath: string
) {

  if (!api.brand_assets_manifest_filename) {
    throw new Error("brand_assets_manifest_filename is missing.");
  }

  const response = await fetch(`${bucketPath}/brands/${api.brand_assets_manifest_filename}`);

  if (response.ok) {
    const brandAssetsManifest = await response.json();
    await loadBrandAssets(resources, brandAssetsManifest, bucketPath);
  } else {
    throw new LiveChatError(`Failed to fetch brand assets.`, {
      url: response.url,
      status: response.status,
      statusText: response.statusText,
      api,
    });
  }
}

export async function performLocalConfigSetup(api: LiveChatApi) {
  if (api.is_local_config) {
    performLocalSetup();
  }
}

if (typeof window !== 'undefined' && window.LiveChat) {
  (async function () {
    const bucketPath = `/livechat`;
    const api: LiveChatApi | undefined = window.LiveChat;
    const manifest = window.LiveChat?.manifest || null;
    const resources = window.LiveChat?.resources;

    if (!api || !manifest || !resources) {
      throw new LiveChatError(
        "There are files missing from the window.LiveChat object. Cannot load LiveChat."
      );
    }

    try {
      await loadUiResources(manifest, resources, bucketPath);
    } catch (error) {
      throw new LiveChatError(`There was a problem loading the LiveChat UI: ${(error as Error).message}`);
    }

    try {
      await loadLivePersonResources(manifest, resources, bucketPath);
    } catch (error) {
      throw new LiveChatError(`There was a problem loading LiveChat's LivePerson assets: ${(error as Error).message}`);
    }

    try {
      await fetchAndLoadBrandAssets(resources, api, bucketPath);
    } catch (error) {
      if (error instanceof LiveChatError) throw error;

      throw new LiveChatError(
        "There was a problem loading the LiveChat brand-specific assets.",
        {
          originalMessage: (error as Error).message,
          config: api,
        }
      );
    }

    try {
      await performLocalConfigSetup(api);
    } catch (error) {
      throw new LiveChatError(`There was a problem loading the LiveChat local configuration: ${(error as Error).message}`);
    }
  })();
}
